import { EAssetJobType } from "../constants/enums"
import { EVMTransformType } from "./propertyEnums"

/**
 * The single point where the analytics contract couples to the job layer.
 *
 * It lives apart from `propertyEnums.ts` on purpose: `../constants/enums` imports
 * `@aix/env` at runtime, and pulling that into the contract core would make the whole
 * contract unloadable from tooling that runs outside a workspace package — which is
 * exactly what the audit and doc generator do. Everything else under `analytics/` is
 * import-free by design; keep it that way.
 *
 * Exhaustive by construction (`Record<EAssetJobType, …>`), so a new job type is a compile
 * error until someone decides how it should read on a dashboard.
 */
export const VM_ASSET_JOB_TYPE_TO_TRANSFORM_TYPE: Record<EAssetJobType, EVMTransformType> = {
  // Generation
  [EAssetJobType.GENERATE_STORYBOARD]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_IMAGE]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_TTS]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_SOUND_EFFECT]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_AUDIO]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_VOICE_CLONE]: EVMTransformType.GENERATE,
  [EAssetJobType.STATIC_AD_INSPIRATION]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_UGC_VIDEO]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_HOOK_VIDEO]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_BROLL_VIDEO]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_VIDEO_EFFECT]: EVMTransformType.GENERATE,

  // Animation — takes a still as input but the output is new motion content, so it
  // reads as generation rather than a transform on a dashboard.
  [EAssetJobType.ANIMATE_IMAGE]: EVMTransformType.GENERATE,
  [EAssetJobType.AI_AVATAR_VIDEO]: EVMTransformType.GENERATE,
  [EAssetJobType.GENERATE_LIPSYNC_VIDEO]: EVMTransformType.GENERATE,

  // Semantic edits
  [EAssetJobType.INSTRUCTION_EDIT]: EVMTransformType.EDIT,
  [EAssetJobType.OBJECT_REMOVAL]: EVMTransformType.EDIT,
  [EAssetJobType.GENERATIVE_EXPAND]: EVMTransformType.EDIT,
  [EAssetJobType.BACKGROUND_REPLACE]: EVMTransformType.EDIT,
  [EAssetJobType.STYLE_TRANSFER]: EVMTransformType.EDIT,

  // Quality lifts
  [EAssetJobType.UPSCALE_IMAGE]: EVMTransformType.ENHANCE,
  [EAssetJobType.ENHANCE_IMAGE]: EVMTransformType.ENHANCE,
  [EAssetJobType.RELIGHT]: EVMTransformType.ENHANCE,

  // Format / structure
  [EAssetJobType.REMOVE_BACKGROUND]: EVMTransformType.CONVERT,
  [EAssetJobType.RESIZE_IMAGE]: EVMTransformType.CONVERT,
  [EAssetJobType.VECTORIZE]: EVMTransformType.CONVERT,
  [EAssetJobType.DECOMPOSE_LAYERS]: EVMTransformType.CONVERT,

  // Composition
  [EAssetJobType.RENDER_VIDEO_EDITOR]: EVMTransformType.COMPOSE,
  [EAssetJobType.WORKFLOW]: EVMTransformType.COMPOSE,
}
